
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SearchQuery } from '../types';
import { useLocalization } from '../hooks/useLocalization';
import { SearchWidget } from './SearchWidget';

interface ProSearchPageProps {
    logApiCall: any;
    isSettingsReady: boolean;
}

const ProSearchPage: React.FC<ProSearchPageProps> = ({ logApiCall, isSettingsReady }) => {
    const navigate = useNavigate();
    const { t, language } = useLocalization();

    const handleSearch = (query: SearchQuery) => {
        navigate('/results', { state: { searchQuery: query } });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 relative overflow-hidden">
            {/* Animated Background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-teal-500/20 to-cyan-500/10 rounded-full blur-3xl animate-pulse" />
                <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-indigo-500/15 to-purple-500/10 rounded-full blur-3xl" style={{ animationDelay: '2s' }} />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-white/[0.03] to-transparent rounded-full" />
            </div>

            {/* Main Content */}
            <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12">
                {/* Header */}
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-1.5 mb-6">
                        <span className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" />
                        <span className="text-white/80 text-xs font-bold uppercase tracking-widest">
                            {language === 'sv' ? 'Professionell Sökning' : 'Pro Search'}
                        </span>
                    </div>
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight mb-4">
                        {language === 'sv' ? 'Avancerad Sökning' : 'Advanced Search'}
                    </h1>
                    <p className="text-lg text-white/60 max-w-2xl mx-auto font-light">
                        {language === 'sv'
                            ? 'Sök med anpassad nationalitet och valuta för att hitta de bästa priserna för din marknad.'
                            : 'Search with custom nationality and currency to find the best prices for your market.'
                        }
                    </p>
                </div>

                {/* Search Widget with Nationality */}
                <div className="w-full max-w-[90rem] px-4">
                    <SearchWidget
                        onSearch={handleSearch}
                        logApiCall={logApiCall}
                        isSettingsReady={isSettingsReady}
                        showNationality={true}
                    />
                </div>

                {/* Info Cards */}
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl w-full px-4">
                    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center hover:bg-white/10 transition-colors">
                        <span className="text-3xl mb-3 block">🌍</span>
                        <h3 className="text-white font-bold text-sm mb-1">
                            {language === 'sv' ? 'Nationalitetsval' : 'Nationality Selection'}
                        </h3>
                        <p className="text-white/50 text-xs">
                            {language === 'sv'
                                ? 'Välj din nationalitet för att se marknadspecifika priser och erbjudanden'
                                : 'Select your nationality to see market-specific prices and offers'
                            }
                        </p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center hover:bg-white/10 transition-colors">
                        <span className="text-3xl mb-3 block">💰</span>
                        <h3 className="text-white font-bold text-sm mb-1">
                            {language === 'sv' ? 'Flervalsvaluta' : 'Multi-Currency'}
                        </h3>
                        <p className="text-white/50 text-xs">
                            {language === 'sv'
                                ? 'Jämför priser i SEK eller EUR beroende på dina behov'
                                : 'Compare prices in SEK or EUR depending on your needs'
                            }
                        </p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center hover:bg-white/10 transition-colors">
                        <span className="text-3xl mb-3 block">✈️</span>
                        <h3 className="text-white font-bold text-sm mb-1">
                            {language === 'sv' ? 'Alla Destinationer' : 'All Destinations'}
                        </h3>
                        <p className="text-white/50 text-xs">
                            {language === 'sv'
                                ? 'Tillgång till hela Tourvisio-nätverket med alla avgångar'
                                : 'Access to the entire Tourvisio network with all departures'
                            }
                        </p>
                    </div>
                </div>

                {/* Footer Badge */}
                <div className="mt-10 text-center">
                    <span className="text-white/30 text-xs font-medium">
                        Powered by Tourvisio API • Lumi Tours
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ProSearchPage;
